import { useState, useEffect } from 'react'
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import type { Insight } from '@/lib/mock-data'

interface EditInsightModalProps {
  open: boolean
  onClose: () => void
  insight: Insight | null
  onSave: (
    insightId: string,
    data: {
      content: string
      sentiment: 'bullish' | 'bearish' | 'neutral'
      ticker?: string
      tags: string[]
    }
  ) => Promise<void>
}

export function EditInsightModal({
  open,
  onClose,
  insight,
  onSave,
}: EditInsightModalProps) {
  const { t } = useTranslation()
  const [content, setContent] = useState('')
  const [sentiment, setSentiment] = useState<'bullish' | 'bearish' | 'neutral'>(
    'neutral'
  )
  const [ticker, setTicker] = useState('')
  const [tags, setTags] = useState('')
  const [isSaving, setIsSaving] = useState(false)

  // Reset form when insight changes
  useEffect(() => {
    if (insight) {
      setContent(insight.content)
      setSentiment(insight.sentiment)
      setTicker(insight.ticker || '')
      setTags(insight.tags.join(', '))
    }
  }, [insight])

  if (!open || !insight) return null

  const handleSave = async () => {
    if (!content.trim()) {
      toast.error(t('contentRequired'))
      return
    }

    setIsSaving(true)
    try {
      const parsedTags = tags
        .split(',')
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0)

      await onSave(insight.id, {
        content: content.trim(),
        sentiment,
        ticker: ticker.trim().toUpperCase() || undefined,
        tags: parsedTags,
      })

      toast.success(t('insightUpdated'))
      onClose()
    } catch (error) {
      console.error('Failed to update insight:', error)
      toast.error(t('failedToUpdateInsight'))
    } finally {
      setIsSaving(false)
    }
  }

  const sentimentOptions = [
    { value: 'bullish', icon: TrendingUp, label: t('bullish'), color: 'emerald' },
    { value: 'neutral', icon: Minus, label: t('neutral'), color: 'neutral' },
    { value: 'bearish', icon: TrendingDown, label: t('bearish'), color: 'red' },
  ] as const

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-neutral-950 border border-white/10 rounded-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">{t('editInsight')}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg text-neutral-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Author Info (read-only) */}
          <div className="text-sm text-neutral-500">
            {t('editingPostBy')}{' '}
            <span className="text-white">@{insight.username}</span>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-neutral-400 mb-2">
              {t('content')}
            </label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('insightPlaceholder')}
              className="min-h-[120px] bg-neutral-900/50 border-white/10 text-white placeholder:text-neutral-500 focus:border-emerald-500/50"
              maxLength={1000}
            />
            <p className="text-xs text-neutral-500 text-right mt-1 font-mono">
              {t('charactersCount', { count: content.length, max: 1000 })}
            </p>
          </div>

          {/* Sentiment */}
          <div>
            <label className="block text-sm font-medium text-neutral-400 mb-2">
              {t('sentiment')}
            </label>
            <div className="flex gap-2">
              {sentimentOptions.map((option) => {
                const Icon = option.icon
                const isSelected = sentiment === option.value
                return (
                  <button
                    key={option.value}
                    onClick={() => setSentiment(option.value)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg border transition-all ${
                      isSelected
                        ? option.color === 'emerald'
                          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                          : option.color === 'red'
                            ? 'bg-red-500/20 border-red-500/50 text-red-400'
                            : 'bg-neutral-500/20 border-neutral-500/50 text-neutral-300'
                        : 'bg-neutral-900/50 border-white/10 text-neutral-500 hover:border-white/20'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Ticker */}
          <div>
            <label className="block text-sm font-medium text-neutral-400 mb-2">
              {t('tickerOptional')}
            </label>
            <Input
              value={ticker}
              onChange={(e) => setTicker(e.target.value.toUpperCase())}
              placeholder="AAPL"
              className="bg-neutral-900/50 border-white/10 text-white placeholder:text-neutral-500 focus:border-emerald-500/50 font-mono"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-neutral-400 mb-2">
              {t('tagsCommaSeparated')}
            </label>
            <Input
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="trading, analysis, tech"
              className="bg-neutral-900/50 border-white/10 text-white placeholder:text-neutral-500 focus:border-emerald-500/50"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-white/10">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !content.trim()}
            className="bg-emerald-600 hover:bg-emerald-500 text-white"
          >
            {isSaving ? t('saving') : t('saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  )
}
